import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import useMediaQuery from '@mui/material/useMediaQuery';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { activateAccountMutation } from '@drivingdoc/api-client/generated/react-query';

import TextInput from 'components/FormInputs/TextInput';
import PasswordRequirements from 'components/PasswordRequirements';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { passwordRequirements } from 'utils/password';

import ActivateHeader from './components/ActivateHeader';

const activationSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[!@#$%^&*]/, 'Password must contain at least one special character (!@#$%^&*)'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ActivationFormData = z.infer<typeof activationSchema>;

export default function Activate() {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  const [apiError, setApiError] = useState<string | null>(null);

  const form = useForm<ActivationFormData>({
    resolver: zodResolver(activationSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const { handleSubmit } = form;

  const activateMutation = useMutation(activateAccountMutation());

  // If no token, redirect to login
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = async (data: ActivationFormData) => {
    setApiError(null);
    try {
      await activateMutation.mutateAsync({
        body: {
          token,
          newPassword: data.password,
        },
      });

      navigate('/login', {
        state: { message: 'Account activated successfully! Please log in.', severity: 'success' },
      });
    } catch (err) {
      setApiError(getApiErrorMessage(err));
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        flexGrow: 1,
        display: 'flex', // make it a flex container
        flexDirection: 'column', // stack children vertically
        justifyContent: 'center', // vertical centering
      }}
    >
      <FormProvider {...form}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
          <Stack gap={4}>
            {/* page header */}
            <ActivateHeader />

            {/* error alert */}
            <Collapse in={!!apiError && !activateMutation.isPending}>
              <Alert variant="outlined" severity="error">
                {apiError}
              </Alert>
            </Collapse>

            <Stack direction={{ xs: 'column', md: 'row' }} gap={4}>
              <Stack
                gap={3}
                sx={{
                  flex: 1,
                  position: 'relative',
                }}
              >
                {/* password field with confirmation */}
                <TextInput name="password" label="Password" type="password" />
                <TextInput name="confirmPassword" label="Confirm Password" type="password" />

                {/* password requirements */}
                {isMd && <PasswordRequirements fieldName="password" requirements={passwordRequirements} />}

                {/* loading indicator */}
                <Collapse in={activateMutation.isPending}>
                  <LinearProgress />
                </Collapse>

                {/* submit button*/}
                <Button size="large" variant="contained" type="submit" disabled={activateMutation.isPending}>
                  Activate Account
                </Button>
              </Stack>

              {/* password requirements */}
              {!isMd && <PasswordRequirements fieldName="password" requirements={passwordRequirements} />}
            </Stack>
          </Stack>
        </Box>
      </FormProvider>
    </Container>
  );
}
